import React from "react";
import type {
  MappingSuggestion,
  PayloadFormat,
  PayloadTargetTemplate,
  TargetPayloadField,
  TransformationType,
  VariableRow,
} from "../types";
import {
  jdxBookingPayloadSchema,
  jdxCargoPayloadSchema,
  type JdxPayloadField,
  type JdxPayloadSchema,
} from "../schemas";

interface Props {
  open: boolean;
  onClose: () => void;
  payload: string;
  setPayload: (value: string) => void;
  formatPayload: () => void;
  payloadIsValid: boolean;
  inputFormat: PayloadFormat;
  setInputFormat: (value: PayloadFormat) => void;
  targetPayloadType: PayloadTargetTemplate;
  setTargetPayloadType: (value: PayloadTargetTemplate) => void;
  selectedTargetPayload: {
    label: string;
    description: string;
    fields: TargetPayloadField[];
  };
  payloadVariables: VariableRow[];
  suggestedPayloadMappings: MappingSuggestion[];
  setManualMappings: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setMappingTransformations: React.Dispatch<
    React.SetStateAction<Record<string, TransformationType>>
  >;
  setMappingConstants: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  bookingPayloadPreview: string;
  missingRequiredMappings: MappingSuggestion[];
}

type SourceMode = "payload" | "fixed";
type ArrayMode = "full" | "count" | "loop";

type GroupedVariable = {
  key: string;
  group: string;
  fields: VariableRow[];
  isArray: boolean;
  count?: string;
};

type MappableField = JdxPayloadField & {
  fixed?: boolean;
  choices?: string[];
};

function getSchema(targetPayloadType: PayloadTargetTemplate): JdxPayloadSchema {
  if (targetPayloadType === "patch-cargo") return jdxCargoPayloadSchema;
  return jdxBookingPayloadSchema;
}

function normalizePath(path: string) {
  return path.replace(/^payload\./, "").replace(/\[\]/g, "");
}

function pathsMatch(a: string, b: string) {
  return normalizePath(a) === normalizePath(b);
}

function flattenFields(fields: JdxPayloadField[]): MappableField[] {
  const result: MappableField[] = [];

  fields.forEach((field) => {
    if (field.type === "object" && field.children?.length) {
      result.push(...flattenFields(field.children));
      return;
    }

    if (field.type === "array" && field.children?.length) {
      result.push(...field.children.map((child) => ({ ...child })));
      return;
    }

    result.push({
      ...field,
      fixed: field.sourceMode === "automatic",
      choices: field.enumValues,
    });
  });

  return result;
}

function getTopLevelArrayFields(schema: JdxPayloadSchema) {
  return schema.fields.filter((field) => field.type === "array" && field.children?.length);
}

function getGroupLabels(fields: MappableField[]) {
  return Array.from(new Set(fields.map((field) => field.group)));
}

function cleanPath(path: string) {
  return path
    .replace(/^payload\./, "")
    .replace(/\[\]/g, "")
    .replace(/\[[0-9]+\]/g, "");
}

function titleCase(value: string) {
  return value
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function friendlyFieldName(path: string) {
  const clean = cleanPath(path);
  const lower = clean.toLowerCase();
  const last = clean.split(".").pop() || clean;

  if (lower.includes("cargo") && lower.includes("date")) return "Laaddatum";
  if (lower.includes("ready") && lower.includes("date")) return "Laaddatum";
  if (lower.includes("pickup") && lower.includes("date")) return "Ophaaldatum";
  if (lower.includes("arrival") && lower.includes("date")) return "Aankomstdatum";
  if (lower.includes("delivery") && lower.includes("date")) return "Leverdatum";
  if (lower.includes("departure") && lower.includes("date")) return "Vertrekdatum";
  if (lower.includes("eta")) return "Verwachte aankomst";
  if (lower.includes("etd")) return "Verwacht vertrek";

  const dictionary: Record<string, string> = {
    bookingNumber: "Boekingsnummer",
    bookingReference: "Boekingsreferentie",
    reference: "Referentie",
    customerReference: "Klantreferentie",
    orderNumber: "Ordernummer",
    orderNo: "Ordernummer",
    customerName: "Klantnaam",
    customerCode: "Klantcode",
    code: "Code",
    name: "Naam",
    modality: "Transportsoort",
    mode: "Transportsoort",
    transportMode: "Transportsoort",
    loadType: "Ladingtype",
    movementType: "Richting",
    incoterm: "Incoterm",
    date: "Datum",
    eventDate: "Datum",
    origin: "Vertrek",
    destination: "Bestemming",
    originPort: "Vertrekhaven",
    destinationPort: "Aankomsthaven",
    pol: "Vertrekhaven",
    pod: "Aankomsthaven",
    portCode: "Havencode",
    unLoCode: "Havencode",
    location: "Locatie",
    containerNo: "Containernummer",
    containerNumber: "Containernummer",
    containerType: "Container type",
    type: "Type",
    isoCode: "Container type",
    quantity: "Aantal",
    gross: "Brutogewicht",
    weight: "Gewicht",
    net: "Nettogewicht",
    volume: "Volume",
    goodsDescription: "Goederenomschrijving",
    description: "Omschrijving",
  };

  return dictionary[last] || titleCase(last);
}

function friendlyGroupName(groupKey: string) {
  const lower = groupKey.toLowerCase();

  if (lower.includes("container")) return "Containers";
  if (lower.includes("package")) return "Packages";
  if (lower.includes("article")) return "Artikelen";
  if (lower.includes("item")) return "Items";
  if (lower.includes("customer")) return "Klant";
  if (lower.includes("origin")) return "Vertrek";
  if (lower.includes("destination")) return "Bestemming";
  if (lower.includes("route")) return "Route";
  if (lower.includes("planning")) return "Planning";
  if (lower.includes("date")) return "Datums";
  if (lower.includes("shipment")) return "Zending";
  if (lower.includes("booking")) return "Boeking";

  return friendlyFieldName(groupKey);
}

function shortValue(value?: string) {
  if (!value) return "";
  if (value.startsWith("[array:")) return "Lijst";
  if (value.length > 54) return `${value.slice(0, 54)}...`;
  return value;
}

function getArrayCount(value?: string) {
  if (!value) return null;
  const match = value.match(/\[array:\s*(\d+)\]/i);
  return match?.[1] || null;
}

function getIndexFromPath(path: string) {
  const bracketMatch = path.match(/\[(\d+)\]/);
  if (bracketMatch?.[1]) return Number(bracketMatch[1]);

  const dotMatch = path.match(/\.(\d+)\./);
  if (dotMatch?.[1]) return Number(dotMatch[1]);

  return null;
}

function isArrayField(variable: VariableRow) {
  return (
    variable.key.includes("[]") ||
    /\[\d+\]/.test(variable.key) ||
    /\.\d+\./.test(variable.key) ||
    variable.value?.startsWith("[array:") ||
    variable.value === "[array]"
  );
}

function getGroupCount(fields: VariableRow[]) {
  for (const field of fields) {
    const explicitCount = getArrayCount(field.value);
    if (explicitCount) return explicitCount;
  }

  const indexes = fields
    .map((field) => getIndexFromPath(field.key))
    .filter((index): index is number => index !== null);

  if (indexes.length > 0) return String(Math.max(...indexes) + 1);

  return undefined;
}

function getGroupKey(variable: VariableRow) {
  const withoutPayload = variable.key.replace(/^payload\./, "");
  const normalized = withoutPayload
    .replace(/\[\d+\]/g, "[]")
    .replace(/\.\d+\./g, ".[].");

  const parts = normalized.split(".").filter(Boolean);

  return parts[0] || "overig";
}

function getFieldLabelInsideGroup(variable: VariableRow, groupKey: string) {
  const withoutPayload = variable.key.replace(/^payload\./, "");
  const normalized = withoutPayload
    .replace(/\[\d+\]/g, "[]")
    .replace(/\.\d+\./g, ".[].");

  const parts = normalized.split(".").filter(Boolean);

  if (parts[0] === groupKey) {
    const rest = parts.slice(1).filter((part) => part !== "[]");
    return friendlyFieldName(rest.join(".") || parts[0]);
  }

  return friendlyFieldName(variable.key);
}

function buildGroupedVariables(variables: VariableRow[]): GroupedVariable[] {
  const grouped = new Map<string, GroupedVariable>();

  variables.forEach((variable) => {
    const groupKey = getGroupKey(variable);
    const arrayLike = isArrayField(variable);

    if (!grouped.has(groupKey)) {
      grouped.set(groupKey, {
        key: groupKey,
        group: friendlyGroupName(groupKey),
        fields: [],
        isArray: arrayLike,
      });
    }

    const group = grouped.get(groupKey);
    if (!group) return;

    group.fields.push(variable);
    group.isArray = group.isArray || arrayLike;
    group.count = getGroupCount(group.fields);
  });

  return Array.from(grouped.values()).sort((a, b) => {
    if (a.isArray && !b.isArray) return -1;
    if (!a.isArray && b.isArray) return 1;
    return a.group.localeCompare(b.group);
  });
}

function getMapping(path: string, mappings: MappingSuggestion[]) {
  return mappings.find((mapping) => pathsMatch(mapping.target.path, path));
}

function fieldIsDone(field: MappableField, mapping?: MappingSuggestion) {
  if (field.fixed) return true;
  return Boolean(mapping?.source || mapping?.constantValue);
}

function FieldRow({
  field,
  mapping,
  payloadVariables,
  groupedVariables,
  setManualMappings,
  setMappingConstants,
}: {
  field: MappableField;
  mapping?: MappingSuggestion;
  payloadVariables: VariableRow[];
  groupedVariables: GroupedVariable[];
  setManualMappings: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setMappingConstants: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}) {
  const initialMode: SourceMode = mapping?.constantValue ? "fixed" : "payload";
  const [mode, setMode] = React.useState<SourceMode>(initialMode);

  const selectedVariable = payloadVariables.find((item) => item.key === mapping?.source);
  const done = fieldIsDone(field, mapping);

  const supportsCount =
    field.path.toLowerCase().includes("quantity") ||
    field.path.toLowerCase().includes("count") ||
    field.label.toLowerCase().includes("aantal");

  const selectedValue =
    mapping?.source ||
    (supportsCount && mapping?.constantValue
      ? `__count__::${mapping.constantValue}`
      : "");

  return (
    <div className="grid gap-3 border-b border-slate-200 px-4 py-4 last:border-b-0 lg:grid-cols-[240px_minmax(0,1fr)_180px_32px] dark:border-white/10">
      <div>
        <div className="flex items-center gap-1 text-sm font-bold text-slate-950 dark:text-white">
          {field.label}
          {field.required && <span className="text-orange-500">*</span>}
        </div>

        <div className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
          {field.description || field.path}
        </div>

        {field.example !== undefined && (
          <div className="mt-2 rounded-lg bg-slate-50 px-2 py-1 font-mono text-[10px] text-slate-500 dark:bg-white/5 dark:text-slate-400">
            voorbeeld: {String(Array.isArray(field.example) ? field.example.join(", ") : field.example)}
          </div>
        )}
      </div>

      <div>
        {field.fixed ? (
          <div className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-200">
            Automatisch gevuld vanuit Foundry
          </div>
        ) : (
          <div className="space-y-2">
            {field.choices && (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setMode("fixed")}
                  className={`rounded-xl px-3 py-2 text-xs font-bold transition ${
                    mode === "fixed"
                      ? "bg-orange-500 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/10 dark:text-slate-300"
                  }`}
                >
                  Vaste waarde
                </button>

                <button
                  type="button"
                  onClick={() => setMode("payload")}
                  className={`rounded-xl px-3 py-2 text-xs font-bold transition ${
                    mode === "payload"
                      ? "bg-orange-500 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/10 dark:text-slate-300"
                  }`}
                >
                  Uit klantdata
                </button>
              </div>
            )}

            {field.choices && mode === "fixed" ? (
              <select
                value={mapping?.constantValue || ""}
                onChange={(event) =>
                  setMappingConstants((current) => ({
                    ...current,
                    [field.path]: event.target.value,
                  }))
                }
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 outline-none focus:border-orange-400 dark:border-white/10 dark:bg-slate-950 dark:text-white"
              >
                <option value="">Kies vaste waarde...</option>
                {field.choices.map((choice) => (
                  <option key={choice} value={choice}>
                    {choice}
                  </option>
                ))}
              </select>
            ) : (
              <select
                value={selectedValue}
                onChange={(event) => {
                  const value = event.target.value;

                  if (value.startsWith("__count__::")) {
                    const [, countValue] = value.split("::");

                    setManualMappings((current) => ({
                      ...current,
                      [field.path]: "",
                    }));

                    setMappingConstants((current) => ({
                      ...current,
                      [field.path]: countValue || "0",
                    }));

                    return;
                  }

                  setMappingConstants((current) => ({
                    ...current,
                    [field.path]: "",
                  }));

                  setManualMappings((current) => ({
                    ...current,
                    [field.path]: value,
                  }));
                }}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 outline-none focus:border-orange-400 dark:border-white/10 dark:bg-slate-950 dark:text-white"
              >
                <option value="">Kies veld uit klantdata...</option>

                {supportsCount &&
                  groupedVariables
                    .filter((group) => group.isArray)
                    .map((group) => {
                      const count = group.count || "1";

                      return (
                        <option
                          key={`${field.path}-count-${group.key}`}
                          value={`__count__::${count}`}
                        >
                          {group.group} → aantal ({count})
                        </option>
                      );
                    })}

                {groupedVariables.map((group) => (
                  <optgroup
                    key={group.key}
                    label={group.isArray ? `${group.group} · lijst` : group.group}
                  >
                    {group.fields
                      .filter((variable) => !variable.value?.startsWith("[array:"))
                      .map((variable) => (
                        <option key={`${field.path}-${variable.key}`} value={variable.key}>
                          {getFieldLabelInsideGroup(variable, group.key)}
                          {variable.value
                            ? ` — ${shortValue(String(variable.value))}`
                            : ""}
                        </option>
                      ))}
                  </optgroup>
                ))}
              </select>
            )}
          </div>
        )}
      </div>

      <div className="text-xs text-slate-500 dark:text-slate-400">
        {selectedVariable?.value ? (
          <>
            <div className="font-bold text-slate-700 dark:text-slate-300">Voorbeeld</div>
            <div className="mt-1">{shortValue(String(selectedVariable.value))}</div>
          </>
        ) : mapping?.constantValue ? (
          <>
            <div className="font-bold text-slate-700 dark:text-slate-300">Vaste waarde</div>
            <div className="mt-1">{mapping.constantValue}</div>
          </>
        ) : field.fixed ? (
          "Uit configuratie"
        ) : (
          "Nog niet gekozen"
        )}
      </div>

      <div className="flex items-center justify-end">
        <div
          className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-black ${
            done
              ? "bg-emerald-500 text-white"
              : field.required
                ? "bg-orange-500 text-white"
                : "bg-slate-200 text-slate-500 dark:bg-white/10 dark:text-slate-300"
          }`}
        >
          {done ? "✓" : "!"}
        </div>
      </div>
    </div>
  );
}

export default function MapperModal({
  open,
  onClose,
  payload,
  setPayload,
  formatPayload,
  payloadIsValid,
  inputFormat,
  setInputFormat,
  targetPayloadType,
  setTargetPayloadType,
  payloadVariables,
  suggestedPayloadMappings,
  setManualMappings,
  setMappingTransformations,
  setMappingConstants,
  bookingPayloadPreview,
  missingRequiredMappings,
}: Props) {
  const [step, setStep] = React.useState<1 | 2>(1);
  const [arrayMode, setArrayMode] = React.useState<Record<string, ArrayMode>>({});
  const sectionRefs = React.useRef<Record<string, HTMLElement | null>>({});
  const mainScrollRef = React.useRef<HTMLDivElement | null>(null);

  if (!open) return null;

  const schema = getSchema(targetPayloadType);
  const fields = flattenFields(schema.fields);
  const groupLabels = getGroupLabels(fields);
  const groupedVariables = buildGroupedVariables(payloadVariables);
  const arrayGroups = groupedVariables.filter((group) => group.isArray);
  const schemaArrayFields = getTopLevelArrayFields(schema);

  const completedFields = fields.filter((field) =>
    fieldIsDone(field, getMapping(field.path, suggestedPayloadMappings))
  ).length;

  const progress = Math.round((completedFields / Math.max(fields.length, 1)) * 100);

  const resetAll = () => {
    setManualMappings({});
    setMappingTransformations({});
    setMappingConstants({});
  };

  const scrollToGroup = (group: string) => {
    setStep(1);

    window.setTimeout(() => {
      const container = mainScrollRef.current;
      const section = sectionRefs.current[group];

      if (!container || !section) return;

      container.scrollTo({
        top: section.offsetTop - 88,
        behavior: "smooth",
      });
    }, 80);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-5 backdrop-blur-sm">
      <div className="max-h-[94vh] w-full max-w-7xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-white/10 dark:bg-slate-950">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-white/10">
          <div>
            <div className="text-xl font-black text-slate-950 dark:text-white">
              {schema.label} invullen
            </div>
            <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              De JDX payload is leidend. Vul elk JDX veld met klantdata, een vaste waarde of Foundry-configuratie.
            </div>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={targetPayloadType}
              onChange={(event) =>
                setTargetPayloadType(event.target.value as PayloadTargetTemplate)
              }
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700 outline-none dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"
            >
              <option value="shipment-booking">Booking payload</option>
              <option value="patch-cargo">Cargo payload</option>
            </select>

            <span
              className={`rounded-full px-3 py-1.5 text-xs font-black ${
                missingRequiredMappings.length === 0
                  ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                  : "bg-orange-500/10 text-orange-700 dark:text-orange-300"
              }`}
            >
              {missingRequiredMappings.length === 0
                ? "Klaar"
                : `${missingRequiredMappings.length} missen`}
            </span>

            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"
            >
              Sluiten
            </button>
          </div>
        </div>

        <div className="grid h-[calc(94vh-73px)] overflow-hidden xl:grid-cols-[300px_minmax(0,1fr)]">
          <aside className="sticky top-0 h-full overflow-auto border-b border-slate-200 bg-slate-50 p-5 xl:border-b-0 xl:border-r dark:border-white/10 dark:bg-slate-900/50">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-950">
              <div className="text-xs font-black uppercase tracking-wide text-slate-400">
                Klant payload
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2">
                <div className="rounded-xl bg-slate-50 p-3 dark:bg-white/5">
                  <div className="text-[10px] font-black uppercase text-slate-400">
                    Status
                  </div>
                  <div
                    className={`mt-1 text-sm font-black ${
                      payloadIsValid ? "text-emerald-600" : "text-red-500"
                    }`}
                  >
                    {payloadIsValid ? "Geldig" : "Ongeldig"}
                  </div>
                </div>

                <div className="rounded-xl bg-slate-50 p-3 dark:bg-white/5">
                  <div className="text-[10px] font-black uppercase text-slate-400">
                    Velden
                  </div>
                  <div className="mt-1 text-sm font-black text-slate-900 dark:text-white">
                    {payloadVariables.length}
                  </div>
                </div>
              </div>

              {arrayGroups.length > 0 && (
                <div className="mt-3 rounded-xl border border-orange-200 bg-orange-50 p-3 dark:border-orange-500/20 dark:bg-orange-500/10">
                  <div className="text-[10px] font-black uppercase text-orange-700 dark:text-orange-300">
                    Lijsten gevonden
                  </div>
                  <div className="mt-2 space-y-1">
                    {arrayGroups.map((group) => (
                      <div
                        key={group.key}
                        className="text-xs font-semibold text-orange-700 dark:text-orange-200"
                      >
                        {group.group}
                        {group.count ? ` · ${group.count} regels` : ""}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <details className="mt-4">
                <summary className="cursor-pointer text-sm font-bold text-slate-600 hover:text-slate-950 dark:text-slate-300 dark:hover:text-white">
                  Payload bekijken
                </summary>

                <textarea
                  value={payload}
                  onChange={(event) => setPayload(event.target.value)}
                  className="mt-3 h-56 w-full rounded-xl border border-slate-200 bg-white p-3 font-mono text-xs leading-5 text-slate-800 outline-none focus:border-orange-400 dark:border-white/10 dark:bg-slate-950 dark:text-slate-100"
                />

                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={formatPayload}
                    className="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"
                  >
                    Netjes maken
                  </button>

                  <select
                    value={inputFormat}
                    onChange={(event) => setInputFormat(event.target.value as PayloadFormat)}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 outline-none dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"
                  >
                    <option value="json">JSON</option>
                    <option value="xml">XML</option>
                    <option value="csv">CSV</option>
                    <option value="edi">EDI</option>
                    <option value="text">Text</option>
                  </select>
                </div>
              </details>
            </div>

            <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-950">
              <div className="text-xs font-black uppercase tracking-wide text-slate-400">
                JDX payload
              </div>

              <div className="mt-2 text-sm font-black text-slate-950 dark:text-white">
                {schema.label}
              </div>

              <div className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                {schema.description}
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-950">
              <div className="text-xs font-black uppercase tracking-wide text-slate-400">
                Voortgang
              </div>

              <div className="mt-2 text-2xl font-black text-slate-950 dark:text-white">
                {progress}%
              </div>

              <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-white/10">
                <div
                  className="h-full rounded-full bg-orange-500 transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>

              <div className="mt-4 space-y-2 text-sm">
                {groupLabels.map((group) => {
                  const groupFields = fields.filter((field) => field.group === group);
                  const done = groupFields.filter((field) =>
                    fieldIsDone(field, getMapping(field.path, suggestedPayloadMappings))
                  ).length;

                  return (
                    <button
                      key={group}
                      type="button"
                      onClick={() => scrollToGroup(group)}
                      className="flex w-full items-center justify-between rounded-xl bg-slate-50 px-3 py-2 text-left font-bold text-slate-700 transition hover:bg-orange-50 hover:text-orange-700 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-orange-500/10 dark:hover:text-orange-300"
                    >
                      <span>{group}</span>
                      <span className="text-xs text-slate-400">
                        {done}/{groupFields.length}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          <main ref={mainScrollRef} className="h-full overflow-auto">
            <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-6 py-4 backdrop-blur dark:border-white/10 dark:bg-slate-950/95">
              <div className="flex items-center justify-between gap-4">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className={`rounded-xl px-4 py-2 text-sm font-black transition ${
                      step === 1
                        ? "bg-orange-500 text-white"
                        : "bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300"
                    }`}
                  >
                    1. Invullen
                  </button>

                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className={`rounded-xl px-4 py-2 text-sm font-black transition ${
                      step === 2
                        ? "bg-orange-500 text-white"
                        : "bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300"
                    }`}
                  >
                    2. Controleren
                  </button>
                </div>

                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl bg-orange-500 px-5 py-2 text-sm font-black text-white transition hover:bg-orange-400"
                >
                  Mapping opslaan
                </button>
              </div>
            </div>

            {step === 1 && (
              <div className="p-6">
                <div className="mb-5 rounded-2xl border border-orange-200 bg-orange-50 p-4 dark:border-orange-500/20 dark:bg-orange-500/10">
                  <div className="text-base font-black text-slate-950 dark:text-white">
                    Vul de JDX payload
                  </div>
                  <div className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
                    Je vult onze JDX velden met klantdata. Lijsten zoals containers en packages
                    worden als template behandeld: je koppelt één keer hoe elke regel gevuld wordt.
                  </div>
                </div>

                <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-white/10">
                  {groupLabels.map((group) => (
                    <section
                      key={group}
                      ref={(element) => {
                        sectionRefs.current[group] = element;
                      }}
                    >
                      <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-white/10 dark:bg-white/5">
                        <div className="text-sm font-black text-slate-950 dark:text-white">
                          {group}
                        </div>
                      </div>

                      {fields
                        .filter((field) => field.group === group)
                        .map((field) => (
                          <FieldRow
                            key={field.path}
                            field={field}
                            mapping={getMapping(field.path, suggestedPayloadMappings)}
                            payloadVariables={payloadVariables}
                            groupedVariables={groupedVariables}
                            setManualMappings={setManualMappings}
                            setMappingConstants={setMappingConstants}
                          />
                        ))}
                    </section>
                  ))}
                </div>

                {schemaArrayFields.length > 0 && (
                  <div className="mt-6 rounded-2xl border border-slate-200 p-4 dark:border-white/10">
                    <div className="text-sm font-black text-slate-950 dark:text-white">
                      Lijstvelden in JDX payload
                    </div>
                    <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      Deze onderdelen zijn arrays in de JDX payload. Je koppelt ze als lijst of
                      als “per regel” template.
                    </div>

                    <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                      {schemaArrayFields.map((array) => {
                        const current = arrayMode[array.path] || "loop";
                        const matchedSourceList = arrayGroups.find((group) =>
                          pathsMatch(group.key, array.path)
                        );

                        return (
                          <div
                            key={array.path}
                            className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5"
                          >
                            <div className="text-sm font-black text-slate-950 dark:text-white">
                              {array.label}
                            </div>

                            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                              {matchedSourceList?.count
                                ? `${matchedSourceList.count} regels gevonden in klantpayload`
                                : "Array in JDX payload"}
                            </div>

                            <div className="mt-3 grid gap-2">
                              {[
                                ["loop", "Per regel vullen"],
                                ["count", "Alleen aantal gebruiken"],
                                ["full", "Volledige lijst overnemen"],
                              ].map(([value, label]) => (
                                <button
                                  key={value}
                                  type="button"
                                  onClick={() =>
                                    setArrayMode((old) => ({
                                      ...old,
                                      [array.path]: value as ArrayMode,
                                    }))
                                  }
                                  className={`rounded-xl px-3 py-2 text-xs font-bold transition ${
                                    current === value
                                      ? "bg-orange-500 text-white"
                                      : "bg-white text-slate-600 hover:bg-slate-100 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-white/10"
                                  }`}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="mt-6 flex gap-3">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="flex-1 rounded-xl bg-orange-500 px-5 py-3 text-sm font-black text-white transition hover:bg-orange-400"
                  >
                    Volgende: controleren
                  </button>

                  <button
                    type="button"
                    onClick={resetAll}
                    className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"
                  >
                    Reset
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="p-6">
                <div
                  className={`rounded-2xl border p-5 ${
                    missingRequiredMappings.length === 0
                      ? "border-emerald-200 bg-emerald-50 dark:border-emerald-500/20 dark:bg-emerald-500/10"
                      : "border-orange-200 bg-orange-50 dark:border-orange-500/20 dark:bg-orange-500/10"
                  }`}
                >
                  <div className="text-lg font-black text-slate-950 dark:text-white">
                    {missingRequiredMappings.length === 0
                      ? "Mapping is klaar"
                      : "Nog niet alles is ingevuld"}
                  </div>

                  <div className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                    {missingRequiredMappings.length === 0
                      ? "Je kunt de mapping opslaan en daarna een test uitvoeren."
                      : "Ga terug naar invullen en koppel de ontbrekende velden."}
                  </div>
                </div>

                <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-950">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-black text-slate-950 dark:text-white">
                        JDX payload preview
                      </div>
                      <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Dit wordt naar JDX gestuurd.
                      </div>
                    </div>

                    <span
                      className={`rounded-full px-3 py-1 text-xs font-black ${
                        missingRequiredMappings.length === 0
                          ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                          : "bg-orange-500/10 text-orange-700 dark:text-orange-300"
                      }`}
                    >
                      {missingRequiredMappings.length === 0 ? "Geldig" : "Niet compleet"}
                    </span>
                  </div>

                  <pre className="mt-4 max-h-[560px] overflow-auto whitespace-pre-wrap rounded-xl border border-slate-200 bg-slate-50 p-4 font-mono text-xs leading-5 text-slate-800 dark:border-white/10 dark:bg-slate-900 dark:text-slate-100">
                    {bookingPayloadPreview}
                  </pre>
                </div>

                <div className="mt-6 flex gap-3">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"
                  >
                    Terug
                  </button>

                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 rounded-xl bg-orange-500 px-5 py-3 text-sm font-black text-white transition hover:bg-orange-400"
                  >
                    Mapping opslaan
                  </button>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}